/**
 * JiraActivityTimeline — twin-line timeline showing prefilled-Jira generation
 * vs. clickthroughs over the selected window. Added 2026-04-24 per user
 * request ("графики созданных джир и сколько из них тыкнуто (таймлайн)") as
 * the narrative follow-up to the Jira prefill funnel KPIs on the Engagement
 * tab: the KPI trio answers "how many", this chart answers "when".
 *
 * Two overlaid lines on a single axis:
 *   - generated (companion/amber) — area under the line, reads as
 *     "potential clickthroughs" / background signal.
 *   - clicked   (primary/blue)   — line on top, the conversion that actually
 *     happened.
 *
 * The single-chart dual-line layout makes the conversion gap visible at a
 * glance (amber fill minus blue line = un-clicked prefill links) instead of
 * forcing the reader to eyeball two stacked charts for the same comparison.
 *
 * Data SSOT: `computeJiraActivityTimeline` in
 * `src/services/analytics/queries.ts`. Aggregation mirrors the Jira funnel
 * KPIs so the per-bucket sums reconcile with the headline numbers.
 */

import * as React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { chartPalette } from '@/lib/chart-palette';
import { formatBucketLabel, type BucketGranularity } from '@/lib/format-bucket';
import { formatCompactNumber } from '@/lib/format';
import { ChartTooltip, useChartTooltipTrigger } from '@/components/charts/ChartTooltip';
import {
  TimelineTotalsBadge,
  sumField,
  type TimelineTotalItem,
} from '@/components/charts/TimelineTotalsBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { JiraActivityPoint } from '@/services/api-types';

export interface JiraActivityTimelineProps {
  data: JiraActivityPoint[];
  loading: boolean;
  granularity: BucketGranularity;
  height?: number;
}

/**
 * Inner recharts render — extracted so the empty/skeleton states can live
 * alongside the card wrapper without nesting ternaries three deep.
 */
function JiraActivityChart({
  data,
  loading,
  granularity,
  height,
}: JiraActivityTimelineProps): React.ReactElement {
  // Mobile tap persistence — same trigger switch used by every other timeline.
  const tooltipTrigger = useChartTooltipTrigger();

  if (loading) {
    return (
      <div
        data-testid="jira-activity-timeline-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        data-testid="jira-activity-timeline-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No Jira prefill activity in selected period
      </div>
    );
  }
  return (
    <div
      data-testid="jira-activity-timeline"
      style={{ width: '100%', height }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucketLabel(v, granularity)}
          />
          <YAxis tickFormatter={(v: number) => formatCompactNumber(v)} />
          <Tooltip
            {...tooltipTrigger}
            cursor={false}
            content={
              <ChartTooltip
                labelFormatter={(v) =>
                  typeof v === 'string'
                    ? formatBucketLabel(v, granularity)
                    : String(v ?? '')
                }
              />
            }
          />
          <Legend />
          {/* `generated` as a filled area — the "potential" backdrop. Amber
               picks up the warning/companion slot in the palette so it stays
               orthogonal to the hero blue used for the click signal. */}
          <Area
            type="monotone"
            dataKey="generated"
            stroke={chartPalette.companion}
            fill={chartPalette.companion}
            fillOpacity={0.18}
            name="Generated"
            isAnimationActive={false}
          />
          {/* `clicked` on top as a solid line — the conversion story. Primary
               blue matches the "Jira clicked" KPI card's colour weight so the
               two surfaces read as the same signal. */}
          <Line
            type="monotone"
            dataKey="clicked"
            stroke={chartPalette.primary}
            strokeWidth={2.5}
            name="Clicked"
            dot={{ r: 2.5, fill: chartPalette.primary, strokeWidth: 0 }}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Wrapper Card — matches the shell used by CostTrend / UsersTrend so the
 * Engagement page reads as a consistent visual grid. Totals pill in the
 * header mirrors the pattern used on every other timeline so the headline
 * sums survive when the user scrolls the chart off-screen.
 */
export function JiraActivityTimeline({
  data,
  loading,
  granularity,
  height = 260,
}: JiraActivityTimelineProps): React.ReactElement {
  return (
    <Card>
      <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Jira prefill activity over time
        </CardTitle>
        <TimelineTotalsBadge
          data-testid="jira-activity-timeline-totals"
          loading={loading}
          items={[
            {
              key: 'generated',
              label: 'Generated',
              color: chartPalette.companion,
              value: sumField(data ?? [], 'generated'),
            },
            {
              key: 'clicked',
              label: 'Clicked',
              color: chartPalette.primary,
              value: sumField(data ?? [], 'clicked'),
            },
          ] satisfies TimelineTotalItem[]}
        />
      </CardHeader>
      <CardContent className="p-4">
        <JiraActivityChart
          data={data}
          loading={loading}
          granularity={granularity}
          height={height}
        />
      </CardContent>
    </Card>
  );
}
